<template>
  <div class="container">
    <areYouSureBox
      v-model:active="showAreYouSure"
      @response="areYouSureResponseHandler"
    />
    <form class="section columns">
      <div class="column is-two-fifths">
        <section v-if="shouldRenderForm">
          <b-field :label="$t('ShopId')">
            <input
              type="text"
              class="input"
              :value="displayedShopId"
              data-test-id="shopIdInput"
              data-testid="shopIdInput"
              readonly
              disabled
            />
          </b-field>
          <b-field :label="$t('ShopName')">
            <b-input
              required
              expanded
              v-model="shop.shopName"
              :placeholder="canEditShop ? $t('ShopNameInput') : ''"
              :validation-message="
                $t('FieldIsRequired', { fieldName: $t('ShopName') })
              "
              data-test-id="shopNameInput"
              :disabled="!canEditShop"
              ref="shopName"
            ></b-input>
          </b-field>
          <b-field :label="$t('ChainName')">
            <b-autocomplete
              expanded
              :placeholder="canEditShop ? $t('ChooseChain') : ''"
              v-model="shop.chainName"
              icon-pack="fas"
              icon="search"
              field="chainName"
              :data="filteredChainName"
              :open-on-focus="true"
              @select="selectChain"
              :disabled="!canEditShop"
              data-test-id="chainName"
            >
              <template #header>
                <a
                class="has-text-info is-size-6"
                @click="displayChainCreation"
                data-test-id="chainCreationModalButton"
                >
                 <span>  {{ $t("CreateChain") }}</span>
                </a>
              </template>
            </b-autocomplete>
          </b-field>
          <b-field
            :label="$t('ShoppingCenterGroupName')"
            v-if="
              shop.shoppingCenterGroupName !== shop.shoppingCenterName &&
                !isNew
            "
          >
            <b-input expanded :value="shop.shoppingCenterGroupName" disabled />
          </b-field>
          <b-field :label="$t('ShoppingCenterName')">
            <b-autocomplete
              expanded
              v-model="shoppingCenterName"
              :placeholder="canEditShop ? $t('ChooseShoppingCenter') : ''"
              icon-pack="fas"
              icon="search"
              :data="shoppingCenterList"
              :custom-formatter="shoppingCenterSelectLabel"
              :open-on-focus="true"
              @select="onShoppingCenterSelected"
              :disabled="!canEditShop"
              data-test-id="shoppingCenterName"
            ></b-autocomplete>
          </b-field>
          <b-field :label="$t('ContractId')">
            <b-input
              expanded
              required
              v-model="shop.shoppingCenterShopId"
              :placeholder="canEditShop ? $t('ContractInput') : ''"
              :disabled="!canEditShop"
              data-test-id="shopContractIdInput"
              :validation-message="
                $t('FieldIsRequired', { fieldName: $t('ContractId') })
              "
              ref="shoppingCenterShopId"
            ></b-input>
          </b-field>
        </section>
      </div>
      <div class="column is-two-fifths">
        <section v-if="shouldRenderForm">
          <b-field :label="$t('ChainShopId')">
            <b-input
              expanded
              v-model="shop.chainShopId"
              :placeholder="canEditShop ? $t('ChainShopIdInput') : ''"
              :disabled="!canEditShop"
            ></b-input>
          </b-field>
          <b-field
          :label="$t('Sections')"
          data-test-id="sectionsWrapper"
          >
            <b-taginput
              v-model="sections"
              :data="translatedSections"
              field="translatedName"
              autocomplete
              :allow-new="false"
              :open-on-focus="true"
              :closable="canEditShop"
              icon-pack="fas"
              icon="search"
              :placeholder="canEditShop ? $t('ChooseSections') : ''"
              :disabled="!canEditShop"
              @typing="getTranslatedSections"
              @input="initializeSections"
              data-test-id="sections"
            >
              <template #header>
                <a
                class="has-text-info is-size-6"
                @click="displaySectionCreation"
                data-test-id="sectionCreationButton"
                >
                 {{ $t("CreateSection") }}
                </a>
              </template>
            </b-taginput>
          </b-field>
          <NotificationGroupsMulti
            :canEdit="canEditShop"
            :editableTypes="['Shop']"
            v-model="notificationGroupsValue"
            @unauthorize="onUnAuthorizedNotificationGroupChange"
            @create-new-group="displayGroupCreation"
          />
          <b-field :label="$t('ShopCreationDate')">
            <b-datepicker
              required
              v-model="shop.creationDate"
              :placeholder="canEditShop ? $t('ChooseDate') : ''"
              :date-formatter="dateToDisplay"
              :disabled="!canEditShop"
              data-test-id="shopCreationDate"
              ref="creationDate"
            ></b-datepicker>
          </b-field>
          <b-field :label="$t('ShopDeactivationDate')">
            <b-datepicker
              v-model="shop.deactivationDate"
              :placeholder="$t('ShopIsNotClosed')"
              :date-formatter="dateToDisplay"
              :disabled="!canEditShop"
              data-test-id="shopDeactivationDate"
            >
              <b-button
                class="button is-danger"
                @click="shop.deactivationDate = null"
              >
                <span>{{ $t("Clear") }}</span>
                <b-icon pack="fas" icon="times"></b-icon>
              </b-button>
            </b-datepicker>
          </b-field>
          <b-field grouped position="is-right">
            <b-field>
              <b-button @click="cancelUpdate">
                {{ canEditShop ? $t("Cancel") : $t("Return") }}
              </b-button>
            </b-field>
            <b-field v-if="canEditShop">
              <b-button
                data-test-id="shopDetailsSubmit"
                @click="startProcessing"
                :disabled="isProcessing"
              >
                {{ $t("Submit") }}
              </b-button>
            </b-field>
          </b-field>
        </section>
      </div>
    </form>
    <section
      data-test-id="GroupRelatedMembers"
      class="section"
      v-if="relatedMembersList.length > 0"
    >
      <p class="title">{{ $t("Members") }}</p>
      <memberAdministrationDetails
        :membersList="membersAdministrationListDetails"
        :isProcessing="isProcessing"
        :canEdit="false"
        :showRelatedItems="false"
      />
    </section>
    <b-modal
      data-test-id="GroupCreationModal"
      v-model="createGroup"
      :can-cancel="true"
      aria-modal
      @close="closeGroupCreation"
    >
      <notificationGroupModal :id="id" @close="closeGroupCreation" />
    </b-modal>
    <b-modal
      v-model="createChain"
      :can-cancel="true"
      aria-modal
      @close="closeChainCreation"
    >
      <chainCreationModal
        @chain-created="chainCreated"
        @close="closeChainCreation"
      />
    </b-modal>
    <b-modal
      v-model="createSection"
      :can-cancel="true"
      aria-modal
      @close="closeSectionCreation"
    >
      <sectionCreationModal
        @section-create-completed="sectionCreated"
        @close="closeSectionCreation"
        :shoppingCenterGroupId="shop.shoppingCenterGroupId ?? 0"
        :canEditShop="canEditShop"
      />
    </b-modal>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, type ComponentPublicInstance, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'
import * as Sentry from '@sentry/browser'
import { useAppStore, type Shop, type Section, type Chain, type NotificationGroup, type Member } from '@/stores/app'
import shopsService from '@/services/shopsService'
import notificationService from '@/services/notificationService'
import membersService from '@/services/membersService'
import routerUtils from '@/utils/routerUtils'
import areYouSureBox from '@/components/AreYouSureBox.vue'
import NotificationGroupsMulti from '@/components/notifications/NotificationGroupsMulti.vue'
import memberAdministrationDetails from '@/components/members/memberAdministrationDetails.vue'
import notificationGroupModal from '@/components/modals/notificationGroupModal.vue'
import chainCreationModal from '@/components/modals/chainCreationModal.vue'
import sectionCreationModal from '@/components/modals/sectionCreationModal.vue'

interface SectionWithTranslation extends Section {
  translatedName: string
}

interface ShoppingCenterType {
  shoppingCenterId: number
  shoppingCenterName: string
  shoppingCenterGroupName: string
  shoppingCenterGroupId?: number
}

interface ExtendedShop {
  shopId?: number
  shopName?: string
  chainId?: number
  chainName?: string
  shoppingCenterId?: number
  shoppingCenterName?: string
  shoppingCenterGroupName?: string
  shoppingCenterGroupId?: number
  sections?: number[] | Section[]
  creationDate?: Date | string
  deactivationDate?: Date | string | null
  chainShopId?: string | null
  shoppingCenterShopId?: string
  groupsRelated?: { groupId: number }[]
  membersRelated?: { memberId: number }[]
  canEditShop?: boolean
  isDisabled?: boolean
  notificationGroups?: number[]
}

interface GroupListItem {
  groupId: number
  groupName: string
  groupType: string
  groupDisplay: string
}

interface ChainCreatedEvent {
  chainId: number
  chainName: string
}

// Props
const props = withDefaults(defineProps<{
  id?: number
  returnTo?: string
}>(), {
  returnTo: '/Shops/admin'
})

// Composables
const { t } = useI18n()
const store = useAppStore()
const instance = getCurrentInstance()

// Refs
const shopName = ref<ComponentPublicInstance>()
const shoppingCenterShopId = ref<ComponentPublicInstance>()
const creationDate = ref<ComponentPublicInstance>()

// Reactive state
const shoppingCenterName = ref<string>('')
const isNew = ref<boolean>(false)
const shop = ref<ExtendedShop>({})
const sections = ref<SectionWithTranslation[]>([])
const processing = ref<boolean>(false)
const showAreYouSure = ref<boolean>(false)
const translatedSections = ref<SectionWithTranslation[]>([])
const originalShop = ref<ExtendedShop>({})
const notificationGroupsValue = ref<GroupListItem[]>([])
const shopUpdated = ref<boolean>(false)
const createGroup = ref<boolean>(false)
const createChain = ref<boolean>(false)
const createSection = ref<boolean>(false)

// Computed properties
const isProcessing = computed<boolean>(() => 
  processing.value || store.getAllowedShopsList === null
)

const routeEditShopId = computed<number | null>(() =>
  typeof props.id === 'number' && Number.isFinite(props.id) ? props.id : null
)

const isEditMode = computed<boolean>(() => routeEditShopId.value !== null)

const shouldRenderForm = computed<boolean>(() =>
  isEditMode.value || isNew.value
)

const displayedShopId = computed<string>(() => {
  if (isNew.value) {
    return t('SystemGenerated')
  }

  if (typeof shop.value.shopId === 'number' && Number.isFinite(shop.value.shopId)) {
    return shop.value.shopId.toString()
  }

  return ''
})

const canEditShop = computed<boolean>(() => 
  Boolean(shop.value.canEditShop)
)

const shoppingCenterList = computed<ShoppingCenterType[]>(() => {
  const centers = store.getShoppingCenters as ShoppingCenterType[] | null
  if (!centers) return []
  
  return centers.filter(option => {
    if (shoppingCenterName.value === undefined) {
      return true
    }
    return shoppingCenterSelectLabel(option)
      .toString()
      .toLowerCase()
      .indexOf(shoppingCenterName.value.toLowerCase()) >= 0
  })
})

const sectionList = computed<SectionWithTranslation[]>(() => {
  const sectionsData = store.getSections || (shop.value.sections as Section[]) || []
  return sectionsData
    .map(section => ({ ...section, translatedName: section.sectionName || '' }))
    .filter(section =>
      !sections.value.some(selectedSection => 
        selectedSection.sectionId === section.sectionId
      )
    )
})

const filteredChainName = computed<Chain[]>(() => {
  let filteredChains: Chain[] = []
  if (shop.value.chainName && store.getChains) {
    filteredChains = store.getChains.filter(option =>
      option.chainName
        .toString()
        .toLowerCase()
        .includes(shop.value.chainName?.toLowerCase() || '')
    )
  }
  return shop.value.chainName ? filteredChains : (store.getChains || [])
})

const toGroupListItem = (group: NotificationGroup): GroupListItem => ({
  groupId: group.groupId,
  groupName: group.groupName,
  groupType: group.groupType ?? 'Shop',
  groupDisplay: `${group.groupId} ${group.groupName}`
})

const relatedMembersList = computed<Member[]>(() => {
  let membersRelated: Member[] = []
  if (store.getMemberList) {
    const relatedGroupIds = notificationGroupsValue.value.map(group => group.groupId)
    membersRelated = store.getMemberList.filter(member =>
      relatedGroupIds.includes(member.groupId || 0)
    )
  }
  return membersRelated
})

const membersAdministrationListDetails = computed(() => {
  // Create compatible member objects for the service
  const compatibleMembers = relatedMembersList.value.map(member => ({
    memberId: member.memberId || 0,
    memberFirstName: member.memberFirstName || '',
    memberEmail: member.memberEmail || '',
    memberPhoneNumber: member.memberPhoneNumber || 0,
    memberPositionName: member.memberPositionName || '',
    memberComment: member.memberComment || '',
    groupName: member.groupName || '',
    groupId: member.groupId || 0,
    shopsRelated: member.shopsRelated || [],
    notificationStrategy: member.notificationStrategy || 0,
    notificationStrategyName: member.notificationStrategyName || '',
    canUpdateMember: member.canUpdateMember || false,
    email: member.email,
    phoneNumber: member.phoneNumber,
    allowPhone: member.allowPhone,
    allowSms: member.allowSms,
    allowWhatsapp: member.allowWhatsapp,
    allowEmail: member.allowEmail,
    positionName: member.positionName,
    comment: member.comment
  }))
  
  return membersService.prepareMembersDetails(
    compatibleMembers,
    (store.getNotificationGroups || []).map(group => ({
      groupId: group.groupId,
      groupName: group.groupName,
      shopIds: group.shopIds
    })),
    store.getStrategies || []
  )
})

// Methods
const selectChain = (option: Chain | null): void => {
  if (option) {
    shop.value.chainId = option.chainId
  } else {
    shop.value.chainId = undefined
  }
}

const initializeChain = (): void => {
  const chainRelated = store.getChains?.find(chain => 
    chain.chainId === shop.value.chainId
  )
  if (chainRelated && shop.value.chainName !== chainRelated.chainName) {
    shop.value.chainName = chainRelated.chainName
  }
}

const onShoppingCenterSelected = (shoppingCenter: ShoppingCenterType | null): void => {
  shop.value.shoppingCenterId = shoppingCenter?.shoppingCenterId || 0
  getTranslatedSections('')
  
  const validCenterDetails = shoppingCenter 
    ? (store.getShoppingCenters as ShoppingCenterType[])?.find(i => 
        i.shoppingCenterId === shoppingCenter.shoppingCenterId
      )
    : null
    
  if (validCenterDetails) {
    shop.value.shoppingCenterName = validCenterDetails.shoppingCenterName
    shop.value.shoppingCenterGroupName = validCenterDetails.shoppingCenterGroupName || 
      t('MissingShoppingCenterGroupName')
    shop.value.shoppingCenterGroupId = validCenterDetails.shoppingCenterGroupId
  }
}

const initializeSections = (): void => {
  translatedSections.value = sectionList.value.filter(option =>
    shop.value.shoppingCenterId === undefined ||
    store.getAllowedShopsList?.find(s => 
      s.shoppingCenterId === shop.value.shoppingCenterId
    )?.shoppingCenterGroupId === option.shoppingCenterGroupId
  )
}

const getTranslatedSections = (text: string): void => {
  translatedSections.value = sectionList.value.filter(option =>
    option.translatedName
      .toString()
      .toLowerCase()
      .indexOf(text.toLowerCase()) >= 0 &&
    (shop.value.shoppingCenterId === undefined ||
      store.getAllowedShopsList?.find(s => 
        s.shoppingCenterId === shop.value.shoppingCenterId
      )?.shoppingCenterGroupId === option.shoppingCenterGroupId)
  )
}

const initializeSectionsAndGroups = (): void => {
  const sectionsToSend = sections.value
    .map(section => section.sectionId)
    .filter((id): id is number => id !== undefined)
  
  shop.value.sections = sectionsToSend
  
  const notificationGroupsToSend = notificationGroupsValue.value
    .filter(grp => grp.groupType === 'Shop')
    .map(grp => grp.groupId)
  
  shop.value.notificationGroups = notificationGroupsToSend
}

const startProcessing = async (): Promise<void> => {
  console.log('startProcessing called')
  console.log('  processing.value:', processing.value)

  if (!processing.value) {
    console.log('Not currently processing, proceeding...')
    processing.value = true

    console.log('Calling initializeSectionsAndGroups...')
    initializeSectionsAndGroups()
    console.log('  shop.value.sections:', shop.value.sections)
    console.log('  shop.value.notificationGroups:', shop.value.notificationGroups)

    // Create a proper shop object for validation - use any to bypass complex type issues
    const shopForValidation: any = {
      ...shop.value,
      chainId: shop.value.chainId || 0,
      shopName: shop.value.shopName || '',
      shoppingCenterShopId: shop.value.shoppingCenterShopId || '',
      creationDate: shop.value.creationDate || new Date(),
      deactivationDate: shop.value.deactivationDate,
      shopId: shop.value.shopId || 0,
      sections: shop.value.sections || []
    }
    console.log('shopForValidation:', shopForValidation)

    console.log('Calling validateUpdateShop...')
    const validationResults = shopsService.validateUpdateShop(
      shopForValidation,
      [shopName.value, shoppingCenterShopId.value, creationDate.value]
    )
    console.log('  validationResults:', validationResults)

    if (validationResults.length > 0) {
      console.log('Validation failed!')
      processing.value = false
      const translatedErrors = validationResults
        .map(validator => t(validator))
        .join(', ')
      console.log('  Errors:', translatedErrors)
      openNotification(translatedErrors, 'is-danger')
    } else {
      console.log('Validation passed! Showing confirmation dialog...')
      showAreYouSure.value = true
      console.log('  showAreYouSure.value:', showAreYouSure.value)
    }
  } else {
    console.log('Already processing, skipping...')
  }
}

const areYouSureResponseHandler = async (action: string): Promise<void> => {
  console.log('areYouSureResponseHandler called with:', action)
  if (showAreYouSure.value) {
    showAreYouSure.value = false
    try {
      if (action === 'yes') {
        console.log('About to call sendUpdate')
        await sendUpdate()
        console.log('sendUpdate completed')
      }
    } finally {
      processing.value = false
    }
  }
}

const dateToDisplay = (date: Date | null): string => {
  if (date) {
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`
  }
  return ''
}

const shoppingCenterSelectLabel = (shop: ShoppingCenterType): string => {
  if (store.getAllowedShopsList) {
    if (shop.shoppingCenterGroupName === shop.shoppingCenterName) {
      return shop.shoppingCenterName || ''
    }
    return `${shop.shoppingCenterGroupName || ''} ${shop.shoppingCenterName || ''}`
  }
  return ''
}

const cancelUpdate = (): void => {
  routerUtils.moveTo(props.returnTo)
}

const sendUpdate = async (): Promise<void> => {
  console.log('sendUpdate called')
  try {
    // Create properly typed shop object for API - using any to bypass strict typing for sections
    const shopToSend: any = {
      shopId: shop.value.shopId,
      shopName: shop.value.shopName,
      chainId: shop.value.chainId,
      chainName: shop.value.chainName,
      shoppingCenterId: shop.value.shoppingCenterId,
      shoppingCenterName: shop.value.shoppingCenterName,
      shoppingCenterGroupName: shop.value.shoppingCenterGroupName,
      shoppingCenterGroupId: shop.value.shoppingCenterGroupId,
      sections: shop.value.sections, // Will be number[] for API
      creationDate: shop.value.creationDate,
      deactivationDate: shop.value.deactivationDate,
      chainShopId: shop.value.chainShopId,
      shoppingCenterShopId: shop.value.shoppingCenterShopId,
      canEditShop: shop.value.canEditShop,
      isDisabled: shop.value.isDisabled,
      notificationGroups: shop.value.notificationGroups
    }

    console.log('shopToSend:', shopToSend)
    console.log('Calling shopsService.postShop...')
    const result = await shopsService.postShop(shopToSend)
    console.log('Response received:', result)

    if (isNew.value) {
      const createdShopId = result?.shopId
      if (typeof createdShopId !== 'number' || createdShopId <= 0) {
        throw new Error('Missing shop id from UpdateShop response')
      }
      shop.value.shopId = createdShopId
    }

    shop.value.sections = sections.value
    openNotification(t('SuccessShopUpdated'), 'is-success')
    notificationService.getNotificationGroups()

    if (shop.value.shopId) {
      // Create compatible shop for store
      const shopForStore = {
        ...shop.value,
        shopId: shop.value.shopId,
        chainId: shop.value.chainId || 0,
        shopName: shop.value.shopName || '',
        creationDate: shop.value.creationDate || new Date(),
        deactivationDate: shop.value.deactivationDate || null
      }
      
      shopsService.addNewShop(shopForStore as any)
    }
    
    shopUpdated.value = true
    routerUtils.moveTo(props.returnTo)
  } catch (error: any) {
    if (error?.response && error.response.status === 409) {
      Object.getOwnPropertyNames(error.response.data?.errors || {}).forEach(err => {
        openNotification(t(err), 'is-danger')
      })
    } else {
      openNotification(t('ErrorContactUs'), 'is-danger')
      Sentry.captureException(error)
    }
  }
}

const createNewShop = (): void => {
  shop.value = {
    canEditShop: true,
    shopName: '',
    chainId: 0,
    chainShopId: '',
    creationDate: new Date(),
    deactivationDate: null,
    isDisabled: false,
    sections: [],
    shoppingCenterShopId: '',
    shoppingCenterId: 0
  }
  sections.value = []
  isNew.value = true
  initializeSections()
}

const openNotification = (message: string, type: string): void => {
  const autoCloseProps = type === 'is-success' 
    ? { autoClose: true, duration: 5000 }
    : {}
    
  if (instance?.appContext.config.globalProperties.$buefy) {
    instance.appContext.config.globalProperties.$buefy.notification.open({
      message,
      type,
      hasIcon: true,
      indefinite: true,
      closable: true,
      position: 'is-top',
      ...autoCloseProps
    })
  }
}

const resetShop = (): void => {
  shop.value = {}
  isNew.value = false
  shopUpdated.value = false
  sections.value = []
  notificationGroupsValue.value = []
  shoppingCenterName.value = ''
  createGroup.value = false
  createChain.value = false
  createSection.value = false
}

const onUnAuthorizedNotificationGroupChange = (groupDetails: GroupListItem): void => {
  openNotification(
    t('UnAuthorizeNotificationGroup', {
      groupName: groupDetails.groupName
    }),
    'is-danger'
  )
}

const revertToOriginalShop = (): void => {
  Object.assign(shop.value, originalShop.value)
}

const getShopToEdit = (): boolean => {
  const foundShop = routeEditShopId.value === null
    ? undefined
    : store.getAllowedShopsList?.find(x => x.shopId === routeEditShopId.value)

  if (foundShop) {
    onShopSelected(foundShop)
    return true
  }

  return false
}

const mapSections = (): void => {
  const shopSections = shop.value.sections as Section[] || []
  sections.value = shopSections.map(section => ({
    ...section,
    translatedName: section.sectionName || ''
  }))
  getTranslatedSections('')
}

const onShopSelected = (selectedShop: Shop): void => {
  if (store.getAllowedShopsList) {
    shop.value = { ...selectedShop }
    originalShop.value = { ...selectedShop }
    mapSections()
    shoppingCenterName.value = selectedShop.shoppingCenterName || ''
  }
}

const displayGroupCreation = (): void => {
  createGroup.value = true
}

const closeGroupCreation = (): void => {
  createGroup.value = false
}

const displayChainCreation = (): void => {
  createChain.value = true
}

const closeChainCreation = (): void => {
  createChain.value = false
}

const displaySectionCreation = (): void => {
  createSection.value = true
}

const closeSectionCreation = (): void => {
  createSection.value = false
}

const chainCreated = (selectedChain: ChainCreatedEvent): void => {
  shop.value.chainId = selectedChain.chainId
  shop.value.chainName = selectedChain.chainName
  closeChainCreation()
}

const sectionCreated = (newSection: Section): void => {
  if (!shop.value.sections) {
    shop.value.sections = []
  }
  (shop.value.sections as Section[]).push(newSection)
  mapSections()
  closeSectionCreation()
}

// Lifecycle hooks
onMounted(async () => {
  if (!store.getAllowedShopsList || store.getAllowedShopsList.length === 0) {
    await shopsService.getAllowedShopsList()
  }
  
  if (!store.getShoppingCenters || store.getShoppingCenters.length === 0) {
    shopsService.setShoppingCenterGroupsList()
  }
  
  if (store.getAllowedShopsList?.some(shop => shop.canEditShop === true)) {
    if (!store.getChains) {
      await shopsService.getChains()
    }
    
    if (!store.getSections || store.getSections.length === 0) {
      await shopsService.getSections()
      initializeSections()
    }
    
    if (!store.getNotificationGroups || store.getNotificationGroups.length === 0) {
      notificationService.getNotificationGroups()
    }
    
    if (!store.getMemberList || store.getMemberList.length === 0) {
      membersService.getMembersList()
    }
    
    if (!store.getStrategies || store.getStrategies.length === 0) {
      notificationService.getStrategies()
    }
  }
  
  if (isEditMode.value) {
    const foundShop = getShopToEdit()
    if (foundShop) {
      initializeChain()
    }
  } else {
    createNewShop()
  }

})

onBeforeUnmount(() => {
  if (!shopUpdated.value) {
    revertToOriginalShop()
  }
  resetShop()
})

// Watchers
watch(
  () => store.getNotificationGroups,
  (newList) => {
    const shopId = props.id
    if (newList && typeof shopId === 'number') {
      notificationGroupsValue.value = newList.filter(group =>
        group.shopIds.includes(shopId)
      ).map(toGroupListItem)
    }
  },
  { immediate: true }
)
</script>
